import React, { useState } from "react";
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  InputAdornment,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@material-ui/core";
import { Visibility, VisibilityOff, FileCopyOutlined } from "@material-ui/icons";
import { Field } from "formik";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { COMPACT_MENU } from "./compactMenu";

/**
 * Identidad de la conexión Baileys: nombre, token de API, estado y predeterminado (doc 0439 S6).
 *
 * El token va en UN solo campo. Guardado, se ve enmascarado con su final (`••••b307`); el ojo lo
 * revela con la MISMA llamada auditada de siempre (`/whatsapp/:id/token`, doc 0434 M12). Escribir
 * encima sigue significando «regenerarlo al guardar», igual que en el modal genérico.
 */
const BaileysIdentitySection = ({ values, setFieldValue }) => {
  const [revelado, setRevelado] = useState(null);
  const [cargando, setCargando] = useState(false);

  const alternarToken = async () => {
    if (revelado !== null) {
      setRevelado(null);
      return;
    }
    setCargando(true);
    try {
      const { data } = await api.get(`/whatsapp/${values.id}/token`);
      setRevelado(data?.token || "");
    } catch (err) {
      toastError(err);
    } finally {
      setCargando(false);
    }
  };

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(revelado);
      toast.success(i18n.t("whatsappModal.tokenCopied"));
    } catch (err) {
      toastError(err);
    }
  };

  const escribiendo = Boolean(values.token);
  const mostrandoGuardado = !escribiendo && revelado !== null;
  const valorCampo = escribiendo ? values.token : mostrandoGuardado ? revelado : "";
  const placeholder = values.tokenPreview ? `••••${String(values.tokenPreview).replace(/^…/, "")}` : "Se genera al guardar";

  return (
    <>
      <Grid item xs={12} sm={6}>
        <Field name="name">
          {({ field, meta }) => (
            <TextField
              {...field}
              label={i18n.t("whatsappModal.form.name")}
              autoFocus
              variant="outlined"
              size="small"
              fullWidth
              required
              error={meta.touched && Boolean(meta.error)}
              helperText={meta.touched && meta.error}
            />
          )}
        </Field>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          label="Token de API"
          variant="outlined"
          size="small"
          fullWidth
          autoComplete="off"
          placeholder={placeholder}
          value={valorCampo}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            readOnly: mostrandoGuardado,
            endAdornment: values.id && values.tokenPreview && !escribiendo ? (
              <InputAdornment position="end">
                {mostrandoGuardado && (
                  <Tooltip title={i18n.t("whatsappModal.tokenCopy")}>
                    <IconButton size="small" onClick={copiar} aria-label="copiar token">
                      <FileCopyOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title={mostrandoGuardado ? "Ocultar" : "Ver el token guardado"}>
                  <span>
                    <IconButton size="small" onClick={alternarToken} disabled={cargando} aria-label="ver token">
                      {cargando ? <CircularProgress size={16} /> : mostrandoGuardado ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              </InputAdornment>
            ) : null,
          }}
          onChange={(e) => {
            setRevelado(null);
            setFieldValue("token", e.target.value);
          }}
          helperText={escribiendo ? "Al guardar se REGENERA el token: las integraciones con el anterior dejarán de funcionar" : " "}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl variant="outlined" size="small" fullWidth>
          <InputLabel>Estado</InputLabel>
          <Field as={Select} name="status" label="Estado" MenuProps={COMPACT_MENU}>
            <MenuItem value="OPENING">Abriendo</MenuItem>
            <MenuItem value="CONNECTED">Conectado</MenuItem>
            <MenuItem value="DISCONNECTED">Desconectado</MenuItem>
          </Field>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6} style={{ display: "flex", alignItems: "center" }}>
        <FormControlLabel
          control={<Field as={Switch} color="primary" size="small" name="isDefault" checked={values.isDefault} />}
          label={i18n.t("whatsappModal.form.default")}
        />
      </Grid>
    </>
  );
};

export default BaileysIdentitySection;
